import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useToast } from "components/ui/Toast/use-toast";
import { logError } from "utility/Function";
import { CoachAuthError } from "apps/user/learner/api/coach";
import {
  ConversationSummary,
  fetchConversations,
} from "apps/user/learner/api/coach-history";
import { authenticationFailed } from "providers/apollo-client";
import { useTranslation } from "lib/i18n/use-translation";
import OriDrawer from "./OriDrawer";
import styles from "./OriHistoryPanel.module.scss";

interface OriHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (conversationIds: string[], dateKey: string) => void;
  selectedDateKey?: string | null;
}

function previewText(
  conversation: ConversationSummary,
  fallback: string
): string {
  return (
    conversation.last_message_content ||
    conversation.last_ori_message ||
    fallback
  );
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}`;
}

const MONTH_FORMAT = new Intl.DateTimeFormat(undefined, { month: "long" });

// One calendar day's worth of Conversation rows, collapsed to a single entry.
// Coach's `/api/conversations/` intentionally returns one row per internal
// segment (mode switches, a completed conversation restarting later the same
// day) — see conversation_lifecycle_service.py. The history list groups them
// back into "one entry per day" the same way mobile's
// `groupConversationsByDay` does, keeping every segment's id so the detail
// view can stitch them into one continuous transcript.
interface DayGroup {
  dateKey: string;
  conversationIds: string[];
  startedAt: string;
  preview: ConversationSummary;
}

function groupByDay(conversations: ConversationSummary[]): DayGroup[] {
  const dayGroups = new Map<string, ConversationSummary[]>();
  for (const conversation of conversations) {
    const date = new Date(conversation.started_at);
    const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    const existing = dayGroups.get(dayKey);
    if (existing) {
      existing.push(conversation);
    } else {
      dayGroups.set(dayKey, [conversation]);
    }
  }

  return Array.from(dayGroups.entries()).map(([dateKey, convs]) => {
    const sortedByStart = [...convs].sort(
      (a, b) =>
        new Date(a.started_at).getTime() - new Date(b.started_at).getTime()
    );
    return {
      dateKey,
      conversationIds: sortedByStart.map((c) => c.id),
      startedAt: sortedByStart[0].started_at,
      preview: sortedByStart[sortedByStart.length - 1],
    };
  });
}

interface MonthSection {
  key: string;
  title: string;
  days: DayGroup[];
}

// Group day-entries into month sections, newest first. Keyed by year+month so
// the same month in different years stays separate; the title shows the month
// name only, matching mobile.
function groupByMonth(conversations: ConversationSummary[]): MonthSection[] {
  const days = groupByDay(conversations).sort(
    (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
  );

  const sections: MonthSection[] = [];
  for (const day of days) {
    const date = new Date(day.startedAt);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const current = sections[sections.length - 1];
    if (current?.key === key) {
      current.days.push(day);
    } else {
      sections.push({ key, title: MONTH_FORMAT.format(date), days: [day] });
    }
  }
  return sections;
}

const OriHistoryPanel: React.FC<OriHistoryPanelProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedDateKey,
}) => {
  const { t } = useTranslation(["ori", "common"]);
  const { openToast } = useToast();
  // openToast/t are fresh references each render; pin them so the fetch
  // effect keys only on `isOpen` and doesn't refetch on every render.
  const openToastRef = useRef(openToast);
  openToastRef.current = openToast;
  const tRef = useRef(t);
  tRef.current = t;
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setHasError(false);

    (async () => {
      try {
        const response = await fetchConversations();
        if (cancelled) return;
        setConversations(response.conversations);
      } catch (error) {
        if (cancelled) return;
        if (error instanceof CoachAuthError) {
          authenticationFailed();
          return;
        }
        logError("Ori conversations fetch failed", error);
        openToastRef.current({
          variant: "error",
          message: tRef.current("ori:history.loadError"),
        });
        setHasError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleSelect = (day: DayGroup) => {
    onSelect(day.conversationIds, day.dateKey);
    onClose();
  };

  return (
    <OriDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t("ori:history.title")}
      closeA11yLabel={t("ori:history.closeA11y")}
      testId="ori-history-panel"
      closeTestId="ori-history-close"
    >
      {isLoading ? (
        <p className={styles.statusCopy} data-testid="ori-history-loading">
          {t("ori:history.loading")}
        </p>
      ) : hasError ? (
        <p className={styles.statusCopy} data-testid="ori-history-error">
          {t("ori:history.loadError")}
        </p>
      ) : conversations.length === 0 ? (
        <p className={styles.statusCopy} data-testid="ori-history-empty">
          {t("ori:history.empty.title")}
        </p>
      ) : (
        groupByMonth(conversations).map((section) => (
          <section key={section.key} className={styles.monthSection}>
            <h3 className={styles.monthHeading}>{section.title}</h3>
            <ul className={styles.list}>
              {section.days.map((day) => (
                <li key={day.dateKey}>
                  <button
                    type="button"
                    className={clsx(
                      styles.item,
                      day.dateKey === selectedDateKey && styles.itemSelected
                    )}
                    onClick={() => handleSelect(day)}
                    data-testid="ori-history-item"
                  >
                    <span className={styles.itemDate}>
                      {formatDate(day.startedAt)}
                    </span>
                    <span className={styles.itemPreview}>
                      {previewText(
                        day.preview,
                        t("ori:history.previewFallback")
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </OriDrawer>
  );
};

export default OriHistoryPanel;
